import { Colors, Lightning, Router } from "@lightningjs/sdk";
import { PAGE } from "../../../lib/utils";
import theme from "../../../lib/theme";
import { TranslatableText } from "../../../components/data-display/TranslatableText/TranslatableText";
import { MaterialIcon } from "../../../components/data-display/MaterialIcon/MaterialIcon";
import { Theme, themeManager } from "../../../components/configurations/Theme/ThemeManager";
import configs from "../../../lib/configs";

interface MenuItemTemplateSpec extends Lightning.Component.TemplateSpec {
    activeId: undefined | string;
    page: PAGE;
    icon: string;
    label: string;
    Icon: {
        Background: object;
        Active: object;
        Inactive: any;
        Label: typeof TranslatableText;
    };
}

export class MenuItem
    extends Lightning.Component<MenuItemTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MenuItemTemplateSpec>
{
    _id = "";
    _link = "";
    _activeItem: undefined | string;

    _inFocus = configs.layout?.menu.fullWidth || false;
    _hasTopIcon = configs.layout.menu.icons;
    _fullWidth = configs.layout.menu.fullWidth;
    _isBottomItem = false;
    _bottomIcons = configs.layout.menu.bottomIcons;

    _shouldShowIcon = true; // Will be calculated based on whether it's a bottom item

    private static _iconSize = configs.layout.menu.fullWidth ? 24 : 36;
    private static _bgSizeW = configs.layout.menu.fullWidth ? 46 : 95;
    static _bgSizeH = configs.layout.menu.fullWidth ? 38 : 75;

    private _themeCallback!: (theme: Theme | undefined) => void;

    static override _template(): Lightning.Component.Template<MenuItemTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            w: this._bgSizeW,
            h: this._bgSizeH,
            Icon: {
                Background: {
                    alpha: 0,
                    texture: Lightning.Tools.getRoundRect(
                        this._bgSizeW,
                        this._bgSizeH,
                        6,
                        0,
                        Colors(theme.colors.focus).alpha(1).get(),
                        true,
                        Colors(theme.colors.focus).alpha(1).get()
                    )
                },
                Inactive:
                    configs.layout.menu.icons || configs.layout.menu.bottomIcons
                        ? {
                              type: MaterialIcon,
                              x: configs.layout.menu.fullWidth ? 0 : this._bgSizeW / 2 - this._iconSize / 2,
                              y: this._bgSizeH / 2,
                              mount: 0.5,
                              h: this._iconSize,
                              w: this._iconSize,
                              alpha: 1
                          }
                        : undefined,
                Label: {
                    type: TranslatableText,
                    key: "",
                    x: this._bgSizeW - 8,
                    y: this._bgSizeH / 2 + 3,
                    mountY: 0.5,
                    pivotX: 0,
                    scaleX: configs.layout.menu.fullWidth ? 1 : 0,
                    alpha: 0,
                    text: {
                        fontSize: theme.menu.font.size,
                        fontFace: "Regular"
                    }
                }
                // Active: { x: 6, y: 6, alpha: 0.01 }
            }
        };
    }

    readonly Icon = this.getByRef("Icon")!;
    readonly Background = this.Icon.getByRef("Background")!;
    // readonly Active = this.Icon.getByRef("Active")!;
    readonly Inactive = this.Icon.getByRef("Inactive")!;
    readonly Label = this.Icon.getByRef("Label")!;

    static get width() {
        return this._bgSizeW;
    }

    static get height() {
        return this._bgSizeH;
    }

    get pageId() {
        return this._id;
    }

    set page(value: PAGE) {
        this._id = value.name;
        this._link = value.link;
        // Check if this is a bottom menu item (info, settings, exit)
        this._isBottomItem = this._id === "info" || this._id === "settings" || this._id === "exit";

        // Determine if icon should be shown based on item type
        this._shouldShowIcon = this._isBottomItem ? this._bottomIcons : this._hasTopIcon;

        // Update label position based on icon visibility
        if (!this._shouldShowIcon) {
            this.Label.patch({
                x: 0
            });
        }
    }

    set icon(value: string) {
        // Show icon based on item type: bottom items use bottomIcons, top items use configs.layout.menu.icons
        this.Inactive.patch({
            // src: Utils.asset(`icons/${value}.png`)
            icon: value,
            color: "#FFFFFF",
            alpha: this._shouldShowIcon ? 1 : 0
        });

        // Update label position based on icon visibility
        if (!this._shouldShowIcon) {
            this.Label.patch({
                x: 0
            });
        } else {
            this.Label.patch({
                x: MenuItem._bgSizeW - 8
            });
        }

        // this.Active.patch({
        //     texture: Lightning.Tools.getSvgTexture(
        //         Utils.asset(`icons/${value}-active.svg`),
        //         this._iconSize,
        //         this._iconSize
        //     )
        // });
    }

    set label(value: string) {
        this.Label.patch({
            key: value
        });
    }

    set activeId(id: string | undefined) {
        this._activeItem = id;
        if (this._activeItem === this._id) {
            this._onFocus();
        } else {
            if (!this.hasFocus()) {
                this._onUnfocus();
            }
        }
    }

    get activeId() {
        return this._activeItem;
    }

    override _init() {
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        if (this._inFocus || this._activeItem === this._id) {
            this.Background.patch({
                texture: Lightning.Tools.getRoundRect(
                    this._inFocus ? theme.menu.activeW - theme.menu.item.offset * 2 : 95,
                    75,
                    2,
                    0,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get(),
                    true,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get()
                ),
                smooth: { alpha: 1, w: this._inFocus ? theme.menu.activeW - theme.menu.item.offset * 2 : 95 }
            });
            this.Label.patch({
                smooth: { scaleX: this._inFocus ? 1 : 0, alpha: this._inFocus ? 1 : 0 }
            });
        }
    }

    override _detach(): void {
        themeManager.unsubscribe(this._themeCallback);
    }

    override _focus() {
        this._onFocus();
    }

    override _unfocus() {
        if (this._inFocus || this._activeItem !== this._id) {
            this._onUnfocus();
        }
    }

    override _handleEnter() {
        if (this._id === "exit") {
            this.application.emit("handleAppExit");
        } else {
            this.application.emit("setActivePage", this._id);

            // When closing filters, push a new state with filters: false and preserve previous state
            const url = new URL(String(window.location.href));
            url.searchParams.delete("filters");
            const previousState = history.state?.previousState || history.state || {};
            history.pushState({ filters: false, previousState }, "", url);

            Router.navigate(this._link);
            Router.focusPage();
        }
    }

    _handleClick() {
        this._handleEnter();
    }

    _onFocus() {
        if (!this._fullWidth) {
            const customTheme = themeManager.getTheme();
            this.Background.patch({
                texture: Lightning.Tools.getRoundRect(
                    this._inFocus ? theme.menu.activeW - theme.menu.item.offset * 2 : 95,
                    75,
                    2,
                    0,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get(),
                    true,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get()
                ),
                smooth: { alpha: 1, w: this._inFocus ? theme.menu.activeW - theme.menu.item.offset * 2 : 95 }
            });
            this.Label.patch({
                smooth: { scaleX: this._inFocus ? 1 : 0, alpha: this._inFocus ? 1 : 0 }
            });
        } else {
            this.Label.patch({
                smooth: { alpha: this.hasFocus() ? 1 : this._inFocus || this.isActive() ? 0.75 : 0.5 }
            });
            if (this.Inactive && this._shouldShowIcon) {
                this.Inactive.patch({
                    smooth: { alpha: this.hasFocus() ? 1 : this._inFocus || this.isActive() ? 0.75 : 0.5 }
                });
            }
        }

        // this.Active.patch({ smooth: { alpha: 1 } });
        // this.Inactive.patch({ smooth: { alpha: 0.01 } });
    }

    _onUnfocus() {
        if (!this._fullWidth) {
            const customTheme = themeManager.getTheme();
            this.Background.patch({
                texture: Lightning.Tools.getRoundRect(
                    95,
                    75,
                    6,
                    0,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get(),
                    true,
                    customTheme?.interaction ?? Colors(theme.colors.focus).get()
                ),
                smooth: {
                    alpha: 0,
                    w: this._inFocus ? theme.menu.activeW - theme.menu.item.offset * 2 : 95
                }
            });
            this.Label.patch({
                smooth: { scaleX: this._inFocus ? 1 : 0, alpha: this._inFocus ? 1 : 0 }
            });
        } else {
            this.Label.patch({
                smooth: { alpha: !this._inFocus && this.isActive() ? 0.75 : 0.5 }
            });
            if (this.Inactive && this._shouldShowIcon) {
                this.Inactive.patch({
                    smooth: { alpha: !this._inFocus && this.isActive() ? 0.75 : 0.5 }
                });
            }
        }

        // this.Active.patch({ smooth: { alpha: 0.01 } });
        // this.Inactive.patch({ smooth: { alpha: 1 } });
    }

    isActive() {
        return this._activeItem === this._id;
    }

    setFocus(value: boolean) {
        this._inFocus = value;
        this.setActiveId();
    }

    setActiveId(id?: string | undefined) {
        if (id) {
            this._activeItem = id;
        }
        if (!this._inFocus && this._activeItem === this._id) {
            this._onFocus();
        } else {
            if (!this.hasFocus()) {
                this._onUnfocus();
            }
        }
    }
}
